import { createReducer, on } from '@ngrx/store';
import {
  IBulkResponse,
  IUser,
  IUserListFilter,
  IUserListSort,
  IUserName,
} from '../../models/user.model';
import { UserActions } from './user.actions';

export interface UserState {
  user: IUser | null; // Allow both IUser and null
  saveSuccess: boolean;
  saveFailure: boolean;
  users: IUser[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  filter: IUserListFilter;
  sort: IUserListSort;
  error: string | null;
  userNames: IUserName[];
  activeSuperVisors: IUserName[]; // Add this
  allSuperVisors: IUserName[]; // Add this
  fileName: string | null; // Add this
  profilePictureUrl: string | null; // Add this
  currentStep: number; // Add this
  userPermission: IUser | null;
  importuserdata: IBulkResponse['data'] | null;
}

export const initialUserState: UserState = {
  user: null, // Add this
  users: [],
  userNames: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  filter: {},
  sort: {
    key: 'createdAt',
    order: 'desc',
  },
  error: null,
  saveSuccess: false,
  saveFailure: false,
  activeSuperVisors: [],
  allSuperVisors: [],
  fileName: null, // Initialize fileName
  profilePictureUrl: null, // Initialize profilePictureUrl
  currentStep: 1, // Initialize currentStep
  userPermission: null,
  importuserdata: null,
};

export const userReducer = createReducer(
  initialUserState,
  // Load Users
  on(UserActions.listUsers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.listUsersSuccess, (state, { response, filters }) => ({
    ...state,
    users: response.data.users.list,
    total: response.data.users.total,
    page: response.data.users.page,
    limit: response.data.users.limit,
    filter: filters.body?.filter ?? state.filter,
    loading: false,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(UserActions.listUsersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(UserActions.listUserNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.listUserNamesSuccess, (state, { response }) => ({
    ...state,
    userNames: response.data.users,
    loading: false,
    error: null,
  })),
  on(UserActions.listUserNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(UserActions.listActiveSuperVisorsNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.listActiveSuperVisorsNamesSuccess, (state, { response }) => ({
    ...state,
    activeSuperVisors: response.data,
    loading: false,
    error: null,
  })),
  on(UserActions.listActiveSuperVisorsNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(UserActions.listAllSuperVisorsNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.listAllSuperVisorsNamesSuccess, (state, { response }) => ({
    ...state,
    allSuperVisors: response.data,
    loading: false,
    error: null,
  })),
  on(UserActions.listAllSuperVisorsNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(UserActions.loadSingleUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.loadSingleUserSuccess, (state, { user }) => ({
    ...state,
    user: user,
    loading: false,
    error: null,
  })),

  on(UserActions.loadUserPermissionSuccess, (state, { userPermission }) => ({
    ...state,
    userPermission: userPermission,
    loading: false,
    error: null,
  })),
  on(UserActions.loadSingleUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(UserActions.clearUserPermissionSuccess, (state) => ({
    ...state,
    userPermission: null,
    loading: false,
    error: null,
  })),

  on(UserActions.createUser, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(UserActions.createUserSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    saveSuccess: true,
  })),
  on(UserActions.createUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    saveFailure: true,
    user: null,
  })),

  // Update User
  on(UserActions.updateUser, (state) => ({
    ...state,
    loading: true,
    error: null,
    saveSuccess: false,
    saveFailure: false,
  })),
  on(UserActions.updateUserSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    saveSuccess: true,
  })),
  on(UserActions.updateUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    user: null,
    error,
    saveFailure: true,
  })),

  // Delete User
  on(UserActions.deleteUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.deleteUserSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(UserActions.deleteUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(UserActions.setUserQueryParams, (state, { params, body }) => ({
    ...state,
    page: params.page ?? state.page,
    limit: params.limit ?? state.limit,
    filter: body?.filter ?? state.filter,
    sort: body?.sort ?? state.sort,
  })),
  on(UserActions.clearSingleUser, (state) => ({
    ...state,
    user: null,
  })),
  on(UserActions.updateProfilePicture, (state, { url, fileName }) => ({
    ...state,
    profilePictureUrl: url,
    fileName: fileName,
  })),
  on(UserActions.updateUserRoles, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.updateUserRolesSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
    user: null,
    saveSuccess: true,
  })),
  on(UserActions.updateUserRolesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    saveFailure: true,
  })),
  on(UserActions.importUsers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.importUsersSuccess, (state, { data }) => ({
    ...state,
    // normalize to an array so components can directly iterate
    importuserdata: data,
    loading: false,
    error: null,
  })),
  on(UserActions.importUsersFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(UserActions.bulkUpdate, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(UserActions.bulkUpdateSuccess, (state, { data }) => ({
    ...state,
    // normalize to an array so components can directly iterate
    importuserdata: data,
    loading: false,
    error: null,
  })),
  on(UserActions.bulkUpdateFailure, (state) => ({
    ...state,
    loading: false,
    error: null,
  }))
);
